/**
 * CSS Validate Tool
 *
 * Validate CSS syntax using prettier's CSS parser.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const CssValidateTool: ITransformerTool = {
  id: 'css-validate',
  name: 'CSS Validate',
  description: 'Validate CSS syntax',
  version: '1.0.0',
  type: 'transformer',
  category: 'Validators & Testers',
  keywords: ['css', 'validate', 'check', 'verify', 'syntax', 'lint'],

  seo: {
    slug: 'css-validator',
    pageTitle: 'CSS Validator - Free Online Syntax Checker',
    metaDescription: 'Validate CSS syntax and catch errors instantly. Powered by prettier CSS parser. Fast, private, runs entirely in your browser.',
    keywords: ['css validator', 'css syntax checker', 'validate css online', 'css lint', 'css checker'],
    relatedTools: ['css-format', 'css-minify'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  async transform(input: string): Promise<string> {
    const text = input.trim();
    if (!text) return '✗ Empty input';

    try {
      const prettierMod = await import('prettier/standalone');
      const pluginMod = await import('prettier/plugins/postcss');

      await prettierMod.format(text, {
        parser: 'css',
        plugins: [pluginMod.default ?? pluginMod],
      });

      const ruleMatches = text.match(/\{[^}]*\}/g);
      const ruleCount = ruleMatches ? ruleMatches.length : 0;
      const propertyMatches = text.match(/[\w-]+\s*:/g);
      const propertyCount = propertyMatches ? propertyMatches.length : 0;
      const selectorMatches = text.match(/[^{}]+(?=\s*\{)/g);
      const selectorCount = selectorMatches ? selectorMatches.length : 0;

      return [
        '✓ Valid CSS',
        `Selectors: ${selectorCount}`,
        `Rule blocks: ${ruleCount}`,
        `Properties: ${propertyCount}`,
        `Size: ${text.length} characters`,
      ].join('\n');
    } catch (e) {
      const error = e as Error;
      const msg = error.message || 'Unknown error';
      const lines = msg.split('\n');
      const relevant = lines.filter(l => !l.includes('at ') || l.includes('>')).slice(0, 10);
      return `✗ Invalid CSS\nError: ${relevant.join('\n')}`;
    }
  },
};
